import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import CART_ITEM from '../../constants/cartItems';
import TrashIcon from '../../../../components/icons/TrashIcon';
import { getAlbumCustomPrintName } from '../../../../util/custom-prints';
import IdentityAvatar from '../../../../containers/global/identity/IdentityAvatarContainer';
import CustomPrintIcon from '../../../../components/icons/CustomPrintIcon';
import { CUSTOM_PRINT_SUBTYPE } from '../../../../constants/pro-custom-prints';
import CustomPrintPlaqueConfiguratorModal from '../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/modal';
import Button from '../../../../components/global/Button';
import { validateCustomPrintPhotoSelections } from '../../../../components/global/CustomPrintConfigurator/CustomPrintPlaqueConfigurator/helpers';
import { isAlbumCustomPrintConfigurableInPlace } from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/helpers';
import AccountCardCartItemPreview from './AccountCardCartItemPreview';
import AccountCardAlbumCustomPrintConfiguratorModal from '../../../../apps/pro/views/HierarchyItem/Matches/components/PurchasingOptions/components/AccountCardAlbumCustomPrintOption/AccountCardAlbumCustomPrintConfigurator/modal';
import CheckoutItemQuantityInput from './CheckoutItemQuantityInput';
import CartItemVendor from './CartItemVendor';
import { getThumbBySize } from '../../../../util/photo';
import { IMAGE_SIZE_CODE } from '../../../../constants/image-size-codes';
import CartItemPhotoPreview from './CartItemPhotoPreview';
import Badge from '../../../../components/global/Badge';

const propTypes = {
  item: PropTypes.shape({
    id: PropTypes.string.isRequired,
    type: PropTypes.oneOf([CART_ITEM.ALBUM_CUSTOM_PRINT]).isRequired,
    purchasedCustomPrint: PropTypes.shape({
      id: PropTypes.string.isRequired,
      album_custom_print: PropTypes.shape({
        id: PropTypes.string,
        custom_print: PropTypes.shape({
          name: PropTypes.string.isRequired,
        }).isRequired,
      }),
    }).isRequired,
    identity: PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
    }).isRequired,
  }).isRequired,
  onRemove: PropTypes.func.isRequired,
  onUpdate: PropTypes.func.isRequired,
  watermarkSettings: PropTypes.shape({
    wallpaperCustomText: PropTypes.string,
    wallpaperFontSize: PropTypes.number,
  }),
};

const AlbumCustomPrintCartItem = ({
  item,
  onRemove,
  onUpdate,
  watermarkSettings,
}) => {
  const [configurationModalOpen, setConfigurationModalOpen] = useState(false);
  const {
    purchasedCustomPrint,
    identity,
    quantity,
    isAllPhotos,
    configuration,
  } = item;
  const { album_custom_print: albumCustomPrint } = purchasedCustomPrint;
  const [basePhoto, teamPhoto] = configuration?.photos ?? [];
  const classes = classNames({
    'checkout-cart-item checkout-album-custom-print-cart-item': true,
  });

  return (
    <div className={classes}>
      <div className="checkout-cart-item-content">
        {(() => {
          if (
            albumCustomPrint.custom_print.subtype ===
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT
          ) {
            return (
              <CartItemPhotoPreview
                photo={basePhoto?.photo}
                watermarkSettings={watermarkSettings}
                className="mr-2"
              >
                {teamPhoto?.photo && (
                  <span className="checkout-cart-item-photo-img-secondary">
                    <img
                      alt=""
                      src={
                        getThumbBySize(
                          teamPhoto?.photo?.thumbnail_urls,
                          IMAGE_SIZE_CODE.SMALL2X,
                        )?.url
                      }
                    />
                  </span>
                )}
              </CartItemPhotoPreview>
            );
          }

          if (item.configuration?.accountCard) {
            return <AccountCardCartItemPreview item={item} />;
          }

          return (
            <div className="checkout-cart-item-figure checkout-cart-item-avatar">
              <IdentityAvatar identity={identity}>
                <span className="checkout-cart-item-photo-icon">
                  <CustomPrintIcon color="#0e84fe" />
                </span>
              </IdentityAvatar>
            </div>
          );
        })()}

        <div className="checkout-cart-item-info">
          <span className="checkout-cart-item-title">
            <span className="text-truncate">
              {getAlbumCustomPrintName(albumCustomPrint)}
            </span>
          </span>
          <Badge className="text-extra-bold" badgeStyle="yellow">
            PAID
          </Badge>
          <CartItemVendor cartItem={item} />
          <div className="checkout-cart-item-info-actions mt-2">
            <CheckoutItemQuantityInput
              onChange={value =>
                onUpdate(item.id, {
                  quantity: value,
                })
              }
              value={quantity}
              removeButtons
            />

            <button
              className="checkout-cart-item-btn-remove"
              onClick={() => onRemove(item.id)}
            >
              <TrashIcon color="#fe5851" size={18} />
            </button>
          </div>
          {albumCustomPrint.custom_print?.subtype ===
            CUSTOM_PRINT_SUBTYPE.SPECIALTY_PRINT && (
            <div className="checkout-cart-item-description">
              This product will be fulfilled by the photographer.
            </div>
          )}
        </div>
      </div>
      {isAlbumCustomPrintConfigurableInPlace(albumCustomPrint) && (
        <Button
          btnSize="sm"
          btnStyle="link"
          className="text-extra-bold text-nowrap"
          onClick={() => setConfigurationModalOpen(true)}
        >
          View &amp; Edit
        </Button>
      )}
      <AccountCardAlbumCustomPrintConfiguratorModal
        defaultAccountCard={item.configuration?.accountCard}
        forceOpen={configurationModalOpen && item.configuration?.accountCard}
        albumCustomPrint={albumCustomPrint}
        albumMembershipGroup={item.albumMembershipGroup}
        onClose={() => setConfigurationModalOpen(false)}
        onFinished={({ accountCard }) => {
          onUpdate(item.id, {
            configuration: { accountCard },
          });
          setConfigurationModalOpen(false);
        }}
        buttonText="Done"
        isAllPhotos={isAllPhotos}
        watermarkSettings={watermarkSettings}
      />
      <CustomPrintPlaqueConfiguratorModal
        forceOpen={configurationModalOpen && !item.configuration?.accountCard}
        albumCustomPrint={albumCustomPrint}
        identity={identity}
        initialConfiguration={item.configuration}
        renderSubmit={config => {
          return (
            <Fragment>
              {validateCustomPrintPhotoSelections(config) && (
                <Button
                  onClick={() => {
                    onUpdate(item.id, {
                      configuration: config,
                    });
                    setConfigurationModalOpen(false);
                  }}
                  btnBlock
                  className="text-black"
                >
                  Done
                </Button>
              )}
              <Button
                btnStyle="link"
                btnBlock
                onClick={() => setConfigurationModalOpen(false)}
                className="text-center mt-2"
              >
                Dismiss
              </Button>
            </Fragment>
          );
        }}
      />
    </div>
  );
};

AlbumCustomPrintCartItem.propTypes = propTypes;

export default AlbumCustomPrintCartItem;
